import { useRef, useState } from 'react';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import UploadFileOutlined from '@mui/icons-material/UploadFileOutlined';
import { useQueryClient } from '@tanstack/react-query';

import { comoErroDaApi } from '../../shared/api/erros';
import { chavesDeGlosas, importarDemonstrativo } from './api/glosas';
import type { DemonstrativoImportado } from './api/tipos';

/** Espelha `GlosaEndpoints.TamanhoMaximoDoDemonstrativo` (`20 * 1024 * 1024`): pré-cheque do front, a API confere de novo. */
export const LIMITE_DE_TAMANHO_BYTES = 20 * 1024 * 1024;

/** Pré-cheque do front: extensão e tamanho. `null` = passou. */
function validarArquivo(arquivo: File): string | null {
  if (arquivo.size > LIMITE_DE_TAMANHO_BYTES) {
    return `Arquivo grande demais: o limite é ${String(Math.floor(LIMITE_DE_TAMANHO_BYTES / (1024 * 1024)))} MB.`;
  }
  if (!arquivo.name.toLowerCase().endsWith('.csv')) {
    return 'Esse arquivo não é um CSV (a extensão não é .csv).';
  }
  return null;
}

/**
 * Importação do demonstrativo de glosa (`TP-054`, T8). O formato é um CSV
 * PROVISÓRIO até chegar o demonstrativo real da operadora (T1): colunas
 * `guia`, `codigo`, `valorGlosado` e, opcionais, `protocolo`, `dataPagamento`,
 * `motivoCodigo`, `motivoTexto`. Os nomes seguem o Demonstrativo de Análise de
 * Conta do Manual TISS; `codigo` vazio é glosa da guia inteira (D13).
 */
export function ImportarDemonstrativo({
  contratoId,
  seletorDeContrato,
  aoImportar,
}: {
  contratoId: string;
  seletorDeContrato: React.ReactNode;
  aoImportar: (demonstrativo: DemonstrativoImportado) => void;
}): React.JSX.Element {
  const cliente = useQueryClient();

  const [competencia, definirCompetencia] = useState('');
  const [arquivo, definirArquivo] = useState<File | null>(null);
  const [recusaLocal, definirRecusaLocal] = useState<string | null>(null);
  const [enviando, definirEnviando] = useState(false);
  const [erroDoEnvio, definirErroDoEnvio] = useState<string | null>(null);
  const [resultado, definirResultado] = useState<{ demonstrativo: DemonstrativoImportado; criado: boolean } | null>(
    null,
  );
  const entradaDeArquivo = useRef<HTMLInputElement>(null);

  const selecionar = (lista: FileList | null): void => {
    const escolhido = lista?.[0];
    if (escolhido === undefined) return;
    definirResultado(null);
    definirErroDoEnvio(null);
    const motivo = validarArquivo(escolhido);
    if (motivo !== null) {
      definirArquivo(null);
      definirRecusaLocal(`"${escolhido.name}": ${motivo}`);
      return;
    }
    definirArquivo(escolhido);
    definirRecusaLocal(null);
  };

  const enviar = (): void => {
    if (arquivo === null || contratoId === '' || competencia === '') return;
    definirEnviando(true);
    definirErroDoEnvio(null);
    definirResultado(null);
    importarDemonstrativo({ contratoId, competencia, arquivo })
      .then((importado) => {
        definirResultado(importado);
        void cliente.invalidateQueries({ queryKey: chavesDeGlosas.raiz });
        aoImportar(importado.demonstrativo);
      })
      .catch((causa: unknown) => {
        definirErroDoEnvio(comoErroDaApi(causa).message);
      })
      .finally(() => {
        definirEnviando(false);
      });
  };

  const podeEnviar = arquivo !== null && contratoId !== '' && competencia !== '' && !enviando;

  return (
    <Stack
      spacing={1.5}
      component="form"
      aria-label="Importar demonstrativo de glosa"
      onSubmit={(evento) => {
        evento.preventDefault();
        enviar();
      }}
    >
      <Typography variant="h3">Importar demonstrativo</Typography>
      <Typography variant="body2" color="text.secondary">
        O demonstrativo de pagamento da operadora, em CSV (formato provisório até o arquivo real
        chegar). Cada linha glosada é classificada pela causa e pelas cláusulas de glosa aprovadas
        no contrato.
      </Typography>

      <Stack direction="row" spacing={1.5} useFlexGap sx={{ flexWrap: 'wrap' }} alignItems="flex-start">
        {seletorDeContrato}
        <TextField
        type="month"
        label="Competência"
        size="small"
        value={competencia}
        onChange={(evento) => {
          definirCompetencia(evento.target.value);
        }}
        slotProps={{ inputLabel: { shrink: true } }}
        sx={{ width: 220 }}
      />
      </Stack>

      {contratoId === '' ? (
        <Typography variant="caption" color="text.secondary">
          Escolha o contrato e a competência do demonstrativo.
        </Typography>
      ) : null}

      <Box
        role="button"
        tabIndex={0}
        aria-label="Área para escolher o CSV do demonstrativo"
        onClick={() => entradaDeArquivo.current?.click()}
        onKeyDown={(evento) => {
          if (evento.key === 'Enter' || evento.key === ' ') entradaDeArquivo.current?.click();
        }}
        sx={(tema) => ({
          border: `2px dashed ${tema.palette.divider}`,
          borderRadius: 1,
          p: 3,
          textAlign: 'center',
          cursor: 'pointer',
        })}
      >
        <input
          ref={entradaDeArquivo}
          type="file"
          accept="text/csv,.csv"
          hidden
          aria-label="Escolher o CSV do demonstrativo"
          onChange={(evento) => {
            selecionar(evento.target.files);
          }}
        />
        <UploadFileOutlined sx={(tema) => ({ fontSize: 32, color: tema.palette.text.secondary })} />
        <Typography variant="body2">Clique para escolher o CSV do demonstrativo.</Typography>
        {arquivo !== null ? (
          <Typography variant="caption" color="text.secondary">
            {arquivo.name}
          </Typography>
        ) : null}
      </Box>

      {recusaLocal !== null ? (
        <Alert severity="warning" role="alert">
          {recusaLocal} Nada foi enviado.
        </Alert>
      ) : null}

      <Box>
        <Button type="submit" variant="contained" disabled={!podeEnviar}>
          Importar
        </Button>
      </Box>

      {erroDoEnvio !== null ? (
        <Alert severity="error" role="alert">
          {erroDoEnvio}
        </Alert>
      ) : null}

      {resultado !== null ? (
        <Alert severity={resultado.criado ? 'success' : 'info'} role="status">
          {resultado.criado
            ? `Demonstrativo importado: ${String(resultado.demonstrativo.linhas)} glosas classificadas, ${String(resultado.demonstrativo.confiancaBaixa)} com confiança baixa.`
            : 'Esse arquivo já tinha sido importado antes. Este é o demonstrativo existente.'}
          {resultado.demonstrativo.semPar > 0
            ? ` ${String(resultado.demonstrativo.semPar)} sem a avaliação de faturamento da guia.`
            : ''}
        </Alert>
      ) : null}
    </Stack>
  );
}
